import axios, { AxiosInstance } from 'axios';
import WebApp from '@twa-dev/sdk';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 10000, // 10 секунд - достаточно после исправления nginx IPv6
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем Telegram Web App init data в каждый запрос для аутентификации
    this.client.interceptors.request.use((config) => {
      if (WebApp.initData) {
        config.headers['X-Telegram-Init-Data'] = WebApp.initData;
      }
      return config;
    });

    // Обработка ошибок с retry logic
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;

        // Retry logic для таймаутов и сетевых ошибок
        if (!config || config.__retryCount >= 2) {
          // Максимум 2 повтора (3 попытки всего)
          if (error.response?.status === 401) {
            console.error('Unauthorized. Invalid Telegram data.');
            WebApp.showAlert('Ошибка авторизации. Попробуйте перезапустить приложение.');
          }
          return Promise.reject(error);
        }

        config.__retryCount = config.__retryCount || 0;

        // Retry только для таймаутов и сетевых ошибок
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response) {
          config.__retryCount += 1;
          console.log(`[ApiClient] Retry ${config.__retryCount}/2 for ${config.url}`);

          // Backoff: 1 секунда между попытками
          await new Promise(resolve => setTimeout(resolve, 1000));

          return this.client.request(config);
        }

        return Promise.reject(error);
      }
    );
  }

  // GET запрос
  async get<T>(url: string, params?: any, noCache: boolean = false): Promise<T> {
    const config: any = { params };

    // Disable cache if requested (for polling)
    if (noCache) {
      config.headers = {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      };
    }

    const response = await this.client.get(url, config);
    return response.data;
  }

  // POST запрос с опциональным кастомным timeout
  async post<T>(url: string, data?: any, customTimeout?: number): Promise<T> {
    const config = customTimeout ? { timeout: customTimeout } : {};
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  // PUT запрос
  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  // DELETE запрос
  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete(url);
    return response.data;
  }

  // Получить baseURL
  getBaseURL(): string {
    return this.client.defaults.baseURL || '';
  }
}

export const apiClient = new ApiClient();
